import {
  type AvailabilityResponse,
  type Lodging,
  type LodgingTravelWalletOffer,
  type WalletOfferApplicabilityResponse,
  lodgingApi,
  type Place,
  type LodgingIds,
  AvailabilityRequestEnum,
  LodgingCollection,
  type DateRange,
  type RoomsSelection,
  type GuestsSelection,
  type Platform,
  type Location,
} from "@b2bportal/lodging-api";
import { axiosInstance } from "@hopper-b2b/api";
import type { IApiConfig } from "@hopper-b2b/types";

/**
 * Normalize the availability response to ensure offer data is consistently
 * available where the UI expects it:
 * - `offers.bestOfferOverall`: falls back to top-level `bestOverallOffer`
 * - `lodging.bestOffer`: falls back to `bestOfferThisLodging` or
 *   `offers.offersByProductId[lodgingId]`
 */
function normalizeOffers(res: AvailabilityResponse): AvailabilityResponse {
  if (!res) return res;

  // bestOverallOffer exists on both Initial and More response types but is
  // separate from offers.bestOfferOverall — it's a top-level convenience field.
  const topLevelOffer: LodgingTravelWalletOffer | undefined =
    res.bestOverallOffer;

  const offers: WalletOfferApplicabilityResponse | undefined = res.offers
    ? {
        ...res.offers,
        bestOfferOverall:
          res.offers.bestOfferOverall ?? topLevelOffer ?? undefined,
      }
    : topLevelOffer
      ? {
          bestOfferOverall: topLevelOffer,
          offersByProductId: {},
          opaqueProductMap: {},
        }
      : undefined;

  const lodgings: Lodging[] | undefined = res.lodgings?.map((lodging) => {
    if (lodging.bestOffer) return lodging;

    const fallbackOffer =
      lodging.bestOfferThisLodging ??
      offers?.offersByProductId?.[lodging.lodging.id];

    return fallbackOffer ? { ...lodging, bestOffer: fallbackOffer } : lodging;
  });

  return {
    ...res,
    offers,
    lodgings: lodgings ?? res.lodgings,
  };
}

interface AvailabilityQuery {
  stayDates: DateRange;
  guests: GuestsSelection;
  rooms: RoomsSelection;
  platform: Platform;
}

export interface AvailabilityByPlaceQuery extends AvailabilityQuery {
  lodgingSelection: { LodgingSelection: "Place" } & Place;
}

export interface AvailabilityByLodgingQuery extends AvailabilityQuery {
  lodgingSelection: { LodgingSelection: "LodgingIds" } & LodgingIds;
}

export interface AvailabilityByLocationQuery extends AvailabilityQuery {
  lodgingSelection: { LodgingSelection: "Location" } & Location;
}

export const fetchAvailabilityByPlaceAPI = async (
  query: AvailabilityByPlaceQuery,
  apiConfig?: IApiConfig
): Promise<AvailabilityResponse> => {
  const body = {
    progressiveConfig: {},
    excludeNonLuxuryLodgings: false,
    AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
    lodgingCollection: LodgingCollection.NoCollection,
    ...query,
  };
  try {
    const res =
      await lodgingApi(axiosInstance).apiV0HotelsAvailabilityPost(body);

    if (res && res.data) {
      return normalizeOffers(res.data);
    } else {
      return undefined;
    }
  } catch (e) {
    console.error("Error fetching availability by place", e);
    return undefined;
  }
};

export const fetchAvailabilityNextPage = async (
  token: string
): Promise<AvailabilityResponse | undefined> => {
  const body = {
    progressiveConfig: {},
    excludeNonLuxuryLodgings: false,
    AvailabilityRequest: AvailabilityRequestEnum.FollowUpSearch,
    moreToken: token,
    lodgingCollection: LodgingCollection.NoCollection,
  };
  try {
    const res =
      await lodgingApi(axiosInstance).apiV0HotelsAvailabilityPost(body);

    if (res && res.data) {
      return normalizeOffers(res.data);
    } else {
      return undefined;
    }
  } catch (e) {
    console.error("Error fetching availability next page", e);
    return undefined;
  }
};

export const fetchAvailabilityByLodgingId = async (
  query: AvailabilityByLodgingQuery,
  apiConfig?: IApiConfig
): Promise<AvailabilityResponse> => {
  const body = {
    progressiveConfig: {},
    excludeNonLuxuryLodgings: false,
    AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
    lodgingCollection: LodgingCollection.NoCollection,
    ...query,
  };
  try {
    const res =
      await lodgingApi(axiosInstance).apiV0HotelsAvailabilityPost(body);

    if (res && res.data) {
      return normalizeOffers(res.data);
    } else {
      return undefined;
    }
  } catch (e) {
    console.error("Error fetching availability by lodging ID", e);
    return undefined;
  }
};

export const fetchAvailabilityByLocation = async (
  query: AvailabilityByLocationQuery
): Promise<AvailabilityResponse> => {
  const body = {
    progressiveConfig: {},
    excludeNonLuxuryLodgings: false,
    AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
    lodgingCollection: LodgingCollection.NoCollection,
    ...query,
  };
  try {
    const res =
      await lodgingApi(axiosInstance).apiV0HotelsAvailabilityPost(body);
    if (res && res.data) {
      return normalizeOffers(res.data);
    } else {
      return undefined;
    }
  } catch (e) {
    console.error("Error fetching availability by location", e);
    return undefined;
  }
};
